/* Welcome Wizard — Post-payment mandatory Communication Seat activation.
 *
 * Spec: /app/memory/SEAT_ACTIVATION_WIZARD_SPEC.md
 *
 * Flow (all N seats, staff seats — admin is NEVER auto-assigned to a seat):
 *   1. Strategy — Option A (same location) | Option B (different per seat)
 *   2. Option A: Country → State → Area code → pick N numbers from pool
 *      (selection order auto-maps to seats)
 *   2. Option B: for each seat in order — blank Country/State/Area/Number
 *   3. Team Setup — single screen, N cards labelled by the phone number
 *      (never "Seat 1/2"), each requires Username + Password (min 6);
 *      Email optional (triggers invite).
 *   4. Done → dashboard
 *
 * No Skip buttons anywhere. Continue disabled until every required field
 * is filled.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  ArrowRight, ArrowLeft, CheckCircle, Phone, MagnifyingGlass,
  Buildings, MapPin,
} from "@phosphor-icons/react";

// US + CA use enumerated dropdowns; other countries use free-text region.
const STATE_LISTS = {
  US: [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
    "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
    "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
  ],
  CA: ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"],
};

const blankLocation = () => ({ country: "", region: "", area_code: "" });

export default function WelcomeWizard() {
  const nav = useNavigate();
  const [seats, setSeats] = useState(null);
  const [phase, setPhase] = useState("loading");
    // loading | choose_mode | select_uniform | select_per_seat | team | done
  const [mode, setMode] = useState(null); // "A" | "B"

  // Option A state
  const [uniform, setUniform] = useState(blankLocation());
  const [uniformResults, setUniformResults] = useState([]);
  const [uniformPicks, setUniformPicks] = useState([]); // ordered array of phone_e164

  // Option B state
  const [perSeatCursor, setPerSeatCursor] = useState(0);
  const [perSeatSearch, setPerSeatSearch] = useState(blankLocation());
  const [perSeatResults, setPerSeatResults] = useState([]);
  const [perSeatPicks, setPerSeatPicks] = useState({}); // seat_id -> { phone_e164, country }

  // Team setup state
  const [teamForm, setTeamForm] = useState({}); // seat_id -> { username, password, email, display_name }

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const loadSeats = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/seats");
      const rows = data.seats || [];
      setSeats(rows);
      const needsNumber = rows.some((s) => s.lifecycle_state === "purchased");
      // Team Setup is no longer part of onboarding — admins assign staff to
      // seats on their own time from the Numbers page.
      if (needsNumber) setPhase("choose_mode");
      else setPhase("done");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setPhase("choose_mode");
    }
  }, []);
  useEffect(() => { loadSeats(); }, [loadSeats]);

  const purchased = useMemo(
    () => (seats || []).filter((s) => s.lifecycle_state === "purchased"),
    [seats],
  );
  // Seats that already have a bound number but no user — need team setup.
  const readyForTeam = useMemo(
    () => (seats || []).filter(
      (s) => s.lifecycle_state === "number_assigned" && !s.assign_target_id,
    ),
    [seats],
  );

  // -----------------------------------------------------------------------
  // Option A — same location for all
  // -----------------------------------------------------------------------
  const uniformCanSearch = uniform.country && uniform.region && uniform.area_code;
  async function searchUniform() {
    if (!uniformCanSearch) return;
    setBusy(true); setErr(""); setUniformResults([]); setUniformPicks([]);
    try {
      const q = new URLSearchParams({
        country: uniform.country,
        type: "local",
        region: uniform.region,
        area_code: uniform.area_code,
        limit: String(Math.max(purchased.length + 10, 20)),
      });
      const { data } = await api.get(`/numbers/search?${q.toString()}`);
      setUniformResults(data.results || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }
  function toggleUniformPick(phone) {
    setUniformPicks((prev) => {
      const idx = prev.indexOf(phone);
      if (idx >= 0) return prev.filter((p) => p !== phone);
      if (prev.length >= purchased.length) return prev; // cap at N
      return [...prev, phone];
    });
  }

  async function confirmUniform() {
    if (uniformPicks.length !== purchased.length) return;
    setBusy(true); setErr("");
    const bindings = purchased.map((seat, i) => ({
      seat_id: seat.id,
      phone_e164: uniformPicks[i],
      country: uniform.country,
    }));
    await bindAll(bindings);
  }

  // -----------------------------------------------------------------------
  // Option B — one location per seat, blank fields between iterations
  // -----------------------------------------------------------------------
  const perSeatCanSearch = perSeatSearch.country && perSeatSearch.region && perSeatSearch.area_code;
  async function searchPerSeat() {
    if (!perSeatCanSearch) return;
    setBusy(true); setErr(""); setPerSeatResults([]);
    try {
      const q = new URLSearchParams({
        country: perSeatSearch.country,
        type: "local",
        region: perSeatSearch.region,
        area_code: perSeatSearch.area_code,
        limit: "20",
      });
      const { data } = await api.get(`/numbers/search?${q.toString()}`);
      setPerSeatResults(data.results || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }
  function pickForCurrentSeat(phone) {
    const seat = purchased[perSeatCursor];
    if (!seat) return;
    setPerSeatPicks({
      ...perSeatPicks,
      [seat.id]: { phone_e164: phone, country: perSeatSearch.country },
    });
    // Reset all inputs for the next iteration.
    setPerSeatSearch(blankLocation());
    setPerSeatResults([]);
    setPerSeatCursor(perSeatCursor + 1);
  }

  const allPerSeatPicked = purchased.length > 0 &&
    purchased.every((s) => perSeatPicks[s.id]);

  async function confirmPerSeat() {
    if (!allPerSeatPicked) return;
    setBusy(true); setErr("");
    const bindings = purchased.map((seat) => ({
      seat_id: seat.id,
      phone_e164: perSeatPicks[seat.id].phone_e164,
      country: perSeatPicks[seat.id].country,
    }));
    await bindAll(bindings);
  }

  async function bindAll(bindings) {
    const results = [];
    for (const b of bindings) {
      try {
        await api.post(`/onboarding/seats/${b.seat_id}/bind-number`, {
          phone_e164: b.phone_e164,
          country: b.country,
        });
        results.push({ ok: true });
      } catch (e) {
        results.push({ ok: false, err: formatApiError(e.response?.data?.detail) || e.message });
      }
    }
    const failures = results.filter((r) => !r.ok);
    if (failures.length > 0) {
      setErr(`${failures.length} of ${results.length} seats failed to bind. ${failures.map((f) => f.err).join(" · ")}`);
      setBusy(false);
      await loadSeats();
      return;
    }
    setBusy(false);
    await loadSeats(); // moves us into the team phase
  }

  // -----------------------------------------------------------------------
  // Team Setup — SINGLE screen with N cards, all submitted together
  // -----------------------------------------------------------------------
  function updateTeamField(seatId, field, value) {
    setTeamForm((prev) => ({
      ...prev,
      [seatId]: { ...(prev[seatId] || {}), [field]: value },
    }));
  }
  const teamAllValid = readyForTeam.length > 0 && readyForTeam.every((s) => {
    const f = teamForm[s.id] || {};
    const username = (f.username || "").trim();
    return username.length >= 2 && (f.password || "").length >= 6;
  });

  async function submitTeam() {
    if (!teamAllValid) return;
    setBusy(true); setErr("");
    const results = [];
    for (const seat of readyForTeam) {
      const f = teamForm[seat.id] || {};
      try {
        await api.post(`/onboarding/seats/${seat.id}/assign-user`, {
          username: f.username.trim(),
          password: f.password,
          email: f.email?.trim() || null,
          display_name: f.display_name?.trim() || null,
        });
        results.push({ ok: true });
      } catch (e) {
        results.push({ seat, ok: false, err: formatApiError(e.response?.data?.detail) || e.message });
      }
    }
    const failures = results.filter((r) => !r.ok);
    setBusy(false);
    if (failures.length > 0) {
      setErr(`${failures.length} assignment${failures.length === 1 ? "" : "s"} failed: ${failures.map((f) => f.err).join(" · ")}`);
      await loadSeats();
      return;
    }
    await loadSeats();
    setPhase("done");
  }

  // -----------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-white" data-testid="welcome-wizard">
      <header className="border-b border-[var(--border)] px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5"><Logo size={36} /></div>
        <div className="text-[12px] text-[var(--text-muted)] uppercase font-mono tracking-wider">
          Mandatory setup
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-8 py-12">
        {phase === "loading" && (
          <div className="text-center py-24" data-testid="phase-loading">
            <div className="w-12 h-12 mx-auto border-4 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* ── STRATEGY CHOICE ─────────────────────────────────────────── */}
        {phase === "choose_mode" && (
          <div data-testid="phase-choose-mode">
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">
              / SEAT ACTIVATION · {purchased.length} SEAT{purchased.length === 1 ? "" : "S"} FOR YOUR TEAM
            </div>
            <h1 className="font-display font-black text-[40px] leading-[1.0] mb-3">
              Activate your<br />Communication Seats.
            </h1>
            <p className="text-[14px] text-[var(--text-secondary)] mb-10 max-w-2xl">
              You've paid for {purchased.length} seat{purchased.length === 1 ? "" : "s"} for your staff.
              Would you like all your Communication Seats to use the same location, or different locations?
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <ModeCard active={mode === "A"} onClick={() => setMode("A")} testid="mode-uniform"
                icon={Buildings} label="Same location for all seats"
                sub={`One country, region and area code. Pick ${purchased.length} number${purchased.length === 1 ? "" : "s"} from a shared pool.`} />
              <ModeCard active={mode === "B"} onClick={() => setMode("B")} testid="mode-per-seat"
                icon={MapPin} label="Different location per seat"
                sub="Choose country, region and area code separately for each seat." />
            </div>
            {err && <div className="mt-6 text-[13px] text-[var(--danger)]" data-testid="mode-error">{err}</div>}
            <div className="mt-10 flex justify-end">
              <button
                disabled={!mode}
                onClick={() => {
                  if (mode === "A") { setUniform(blankLocation()); setUniformResults([]); setUniformPicks([]); setPhase("select_uniform"); }
                  else { setPerSeatCursor(0); setPerSeatSearch(blankLocation()); setPerSeatResults([]); setPerSeatPicks({}); setPhase("select_per_seat"); }
                }}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="mode-continue"
              >
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── OPTION A — same location for all ────────────────────────── */}
        {phase === "select_uniform" && (
          <div data-testid="phase-uniform">
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">
              / OPTION A · SAME LOCATION FOR ALL
            </div>
            <h1 className="font-display font-black text-[32px] leading-[1.05] mb-6">
              Pick your location, then choose {purchased.length} number{purchased.length === 1 ? "" : "s"}.
            </h1>

            <LocationFields
              value={uniform}
              onChange={setUniform}
              testidPrefix="uniform"
            />

            <div className="mt-4 mb-8">
              <button
                disabled={!uniformCanSearch || busy}
                onClick={searchUniform}
                className="btn-secondary inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="uniform-search"
              >
                <MagnifyingGlass size={16} /> {busy ? "Searching…" : "Search available numbers"}
              </button>
            </div>

            {uniformResults.length > 0 && (
              <>
                <div className="text-[12px] text-[var(--text-muted)] uppercase font-mono tracking-wider mb-2">
                  Pick exactly {purchased.length} — selection order maps to your seats.
                  {" "}<span data-testid="uniform-picked-count">{uniformPicks.length}</span> / {purchased.length} selected
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-8" data-testid="uniform-results">
                  {uniformResults.map((num) => {
                    const idx = uniformPicks.indexOf(num.phone_e164);
                    const picked = idx >= 0;
                    const atCap = !picked && uniformPicks.length >= purchased.length;
                    return (
                      <button
                        key={num.phone_e164}
                        onClick={() => toggleUniformPick(num.phone_e164)}
                        disabled={atCap}
                        className={`p-3 border font-mono text-[13px] text-left transition-colors ${
                          picked
                            ? "border-[var(--accent)] bg-[var(--bg-sidebar)] text-[var(--accent)]"
                            : atCap
                              ? "border-[var(--border)] opacity-40 cursor-not-allowed"
                              : "border-[var(--border)] hover:bg-[var(--bg-sidebar)]"
                        }`}
                        data-testid={`uniform-num-${num.phone_e164}`}
                      >
                        {picked && (
                          <span className="inline-flex items-center gap-1 mr-1">
                            <CheckCircle size={12} weight="fill" />
                            <span className="text-[10px]">#{idx + 1}</span>
                          </span>
                        )}
                        {num.phone_e164}
                        <div className="text-[11px] text-[var(--text-muted)] mt-1">
                          {num.locality || num.region || ""}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {err && <div className="mb-6 text-[13px] text-[var(--danger)]" data-testid="uniform-error">{err}</div>}

            <div className="flex justify-between">
              <button
                onClick={() => setPhase("choose_mode")}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="uniform-back"
              >
                <ArrowLeft size={16} /> Back
              </button>
              <button
                disabled={uniformPicks.length !== purchased.length || busy}
                onClick={confirmUniform}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="uniform-confirm"
              >
                {busy ? "Activating…" : `Confirm & activate ${purchased.length} seat${purchased.length === 1 ? "" : "s"}`}
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── OPTION B — different location per seat ──────────────────── */}
        {phase === "select_per_seat" && (
          <div data-testid="phase-per-seat">
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">
              / OPTION B · SEAT {Math.min(perSeatCursor + 1, purchased.length)} OF {purchased.length}
            </div>
            {perSeatCursor < purchased.length ? (
              <>
                <h1 className="font-display font-black text-[32px] leading-[1.05] mb-6">
                  Choose a location and number for the next seat.
                </h1>
                <LocationFields
                  value={perSeatSearch}
                  onChange={setPerSeatSearch}
                  testidPrefix="perseat"
                />
                <div className="mt-4 mb-6">
                  <button
                    disabled={!perSeatCanSearch || busy}
                    onClick={searchPerSeat}
                    className="btn-secondary inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="perseat-search"
                  >
                    <MagnifyingGlass size={16} /> {busy ? "Searching…" : "Search"}
                  </button>
                </div>
                {perSeatResults.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6" data-testid="perseat-results">
                    {perSeatResults.map((num) => (
                      <button
                        key={num.phone_e164}
                        onClick={() => pickForCurrentSeat(num.phone_e164)}
                        className="p-3 border border-[var(--border)] hover:border-[var(--accent)] font-mono text-[13px] text-left"
                        data-testid={`perseat-num-${num.phone_e164}`}
                      >
                        {num.phone_e164}
                        <div className="text-[11px] text-[var(--text-muted)] mt-1">
                          {num.locality || num.region || ""}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <h1 className="font-display font-black text-[32px] leading-[1.05] mb-3">
                  Review & activate.
                </h1>
                <p className="text-[14px] text-[var(--text-secondary)] mb-8">
                  All {purchased.length} seat{purchased.length === 1 ? "" : "s"} have a number assigned. Confirm to purchase and bind.
                </p>
                <div className="grid gap-3 mb-8" data-testid="perseat-summary">
                  {purchased.map((seat, i) => (
                    <div key={seat.id} className="p-4 border border-[var(--border)] flex items-center gap-4">
                      <div className="font-mono text-[11px] text-[var(--text-muted)] uppercase tracking-widest">
                        Seat {i + 1}
                      </div>
                      <div className="font-mono text-[15px] text-[var(--text-primary)]">
                        {perSeatPicks[seat.id]?.phone_e164}
                      </div>
                      <div className="text-[11px] text-[var(--text-muted)] ml-auto">
                        {perSeatPicks[seat.id]?.country}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {err && <div className="mb-6 text-[13px] text-[var(--danger)]" data-testid="perseat-error">{err}</div>}

            <div className="flex justify-between">
              <button
                onClick={() => {
                  if (perSeatCursor > 0) {
                    // Roll back the last pick so the admin can redo it.
                    const prevSeat = purchased[perSeatCursor - 1];
                    if (prevSeat) {
                      const next = { ...perSeatPicks };
                      delete next[prevSeat.id];
                      setPerSeatPicks(next);
                    }
                    setPerSeatSearch(blankLocation());
                    setPerSeatResults([]);
                    setPerSeatCursor(perSeatCursor - 1);
                  } else {
                    setPhase("choose_mode");
                  }
                }}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="perseat-back"
              >
                <ArrowLeft size={16} /> Back
              </button>

              {allPerSeatPicked ? (
                <button
                  disabled={busy}
                  onClick={confirmPerSeat}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid="perseat-confirm"
                >
                  {busy ? "Activating…" : `Confirm & activate ${purchased.length} seat${purchased.length === 1 ? "" : "s"}`}
                  <ArrowRight size={16} />
                </button>
              ) : (
                <div className="text-[13px] text-[var(--text-muted)] self-center" data-testid="perseat-progress">
                  {Object.keys(perSeatPicks).length} of {purchased.length} seats assigned
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TEAM SETUP — single screen, all seats at once ───────────── */}
        {phase === "team" && (
          <div data-testid="phase-team">
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">
              / TEAM SETUP · {readyForTeam.length} SEAT{readyForTeam.length === 1 ? "" : "S"}
            </div>
            <h1 className="font-display font-black text-[36px] leading-[1.05] mb-3">
              Who will use each business number?
            </h1>
            <p className="text-[14px] text-[var(--text-secondary)] mb-8 max-w-2xl">
              Set a username and password for each staff member. Optionally add an email to send them an invitation. All fields are required except email.
            </p>

            <div className="grid gap-4 mb-8">
              {readyForTeam.map((seat) => {
                const f = teamForm[seat.id] || {};
                return (
                  <div
                    key={seat.id}
                    className="p-6 border border-[var(--border)] bg-white"
                    data-testid={`team-card-${seat.phone_e164}`}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <Phone size={16} weight="fill" className="text-[var(--accent)]" />
                      <div className="font-mono text-[16px] font-semibold text-[var(--text-primary)]">
                        {seat.phone_e164}
                      </div>
                      <div className="font-mono text-[10px] tracking-widest text-[var(--text-muted)] uppercase ml-auto">
                        Business Number
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <TeamField
                        label="Username" required
                        testid={`team-username-${seat.phone_e164}`}
                        value={f.username || ""}
                        onChange={(v) => updateTeamField(seat.id, "username", v)}
                      />
                      <TeamField
                        label="Password (min 6)" type="password" required
                        testid={`team-password-${seat.phone_e164}`}
                        value={f.password || ""}
                        onChange={(v) => updateTeamField(seat.id, "password", v)}
                      />
                      <TeamField
                        label="Display name (optional)"
                        testid={`team-display_name-${seat.phone_e164}`}
                        value={f.display_name || ""}
                        onChange={(v) => updateTeamField(seat.id, "display_name", v)}
                      />
                      <TeamField
                        label="Email (optional — sends invite)" type="email"
                        testid={`team-email-${seat.phone_e164}`}
                        value={f.email || ""}
                        onChange={(v) => updateTeamField(seat.id, "email", v)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {err && <div className="mb-6 text-[13px] text-[var(--danger)]" data-testid="team-error">{err}</div>}

            <div className="flex justify-end">
              <button
                disabled={!teamAllValid || busy}
                onClick={submitTeam}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="team-submit"
              >
                {busy ? "Assigning…" : "Assign staff & finish"}
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── DONE ────────────────────────────────────────────────────── */}
        {phase === "done" && (
          <div data-testid="phase-done" className="text-center py-16">
            <CheckCircle size={64} weight="fill" className="text-[var(--success)] mx-auto mb-6" />
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ NUMBERS ACTIVE</div>
            <h1 className="font-display font-black text-[36px] leading-[1.0] mb-3">You're all set.</h1>
            <p className="text-[14px] text-[var(--text-secondary)] mb-4 max-w-md mx-auto">
              Your Communication Seats are provisioned. When you're ready, head to <strong>Numbers</strong> to assign each seat to a staff member — you can do that any time.
            </p>
            <p className="text-[12px] text-[var(--text-muted)] mb-10 max-w-md mx-auto">
              Assignment isn't required to explore the dashboard.
            </p>
            <button
              onClick={() => nav("/app")}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="wizard-finish"
            >
              Go to dashboard <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ModeCard({ active, onClick, icon: Icon, label, sub, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-7 border transition-colors ${
        active
          ? "border-[var(--accent)] bg-[var(--bg-sidebar)]"
          : "border-[var(--border)] bg-white hover:bg-[var(--bg-sidebar)]"
      }`}
    >
      <Icon size={28} weight="duotone" className={active ? "text-[var(--accent)]" : "text-[var(--text-primary)]"} />
      <div className="font-display font-bold text-[20px] mt-3">{label}</div>
      <div className="text-[13px] text-[var(--text-secondary)] mt-1.5 leading-relaxed">{sub}</div>
    </button>
  );
}

function LocationFields({ value, onChange, testidPrefix }) {
  const stateList = STATE_LISTS[value.country];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div>
        <label className="label">Country</label>
        <select
          className="input"
          value={value.country}
          onChange={(e) => onChange({ ...value, country: e.target.value, region: "" })}
          data-testid={`${testidPrefix}-country`}
        >
          <option value="">Select…</option>
          <option value="US">United States</option>
          <option value="CA">Canada</option>
          <option value="GB">United Kingdom</option>
          <option value="IN">India</option>
        </select>
      </div>
      <div>
        <label className="label">State / Region</label>
        {stateList ? (
          <select
            className="input"
            value={value.region}
            onChange={(e) => onChange({ ...value, region: e.target.value })}
            data-testid={`${testidPrefix}-region`}
          >
            <option value="">Select…</option>
            {stateList.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <input
            className="input"
            value={value.region}
            onChange={(e) => onChange({ ...value, region: e.target.value })}
            placeholder="e.g. England, Maharashtra"
            data-testid={`${testidPrefix}-region`}
          />
        )}
      </div>
      <div>
        <label className="label">Area code</label>
        <input
          className="input"
          value={value.area_code}
          onChange={(e) => onChange({ ...value, area_code: e.target.value.replace(/\D/g, "") })}
          placeholder="e.g. 415"
          inputMode="numeric"
          data-testid={`${testidPrefix}-area`}
        />
      </div>
    </div>
  );
}

function TeamField({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="label">
        {label}{required && <span className="text-[var(--danger)]"> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input w-full"
        data-testid={testid}
      />
    </div>
  );
}
